import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faArrowDown, 
  faGraduationCap, 
  faBriefcase, 
  faChevronLeft,
  faChevronRight,
  faQuoteLeft,
  faExclamationTriangle,
  faTimes,
  faCheckCircle,
  faChalkboardTeacher,
  faFileAlt,
  faBullseye
} from "@fortawesome/free-solid-svg-icons";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Logo from "../components/common/logo";

import INFO from "../data/user";
import SEO from "../data/seo";

import "./styles/why-us.css";

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let start = 0;
          const increment = end / (duration / 16);
          
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return (
    <span ref={counterRef} className="animated-counter">
      {count}{suffix}
    </span>
  );
};


// Testimonials Carousel
const TestimonialsCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const testimonials = [
    {
      quote: "Logan provided statistical support for a large clinical training project at our hospital. From start to finish, he was wonderful to work with on this project. Logan was easy to communicate with and was able to complete his statistical analysis following the project timeline. He was also able to effectively use data from prior statistics consultants and bring it all together to show our final projects results. I would highly recommend working with Logan.",
      author: "Lead Researcher",
      title: "Psychiatric Hospital"
    },
    {
      quote: "Want to leave a review?",
      author: "Email us!",
      title: "@sbstatisticalconsulting@gmail.com"
    }
  ];

  const nextTestimonial = React.useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const interval = setInterval(nextTestimonial, 15000);
    return () => clearInterval(interval);
  }, [nextTestimonial]);

  return (
    <div className="testimonials-carousel">
      <div className="carousel-container">
        <button className="carousel-btn prev" onClick={prevTestimonial}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        
        <div className="testimonial-content">
          <FontAwesomeIcon icon={faQuoteLeft} className="quote-icon" />
          <p className="testimonial-text">"{testimonials[currentIndex].quote}"</p>
          <div className="testimonial-author">
            <div className="author-name">{testimonials[currentIndex].author}</div>
            <div className="author-title">{testimonials[currentIndex].title}</div>
          </div>
        </div>
        
        <button className="carousel-btn next" onClick={nextTestimonial}>
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
      
      <div className="carousel-dots">
        {testimonials.map((_, index) => (
          <button
            key={index}
            className={`dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => setCurrentIndex(index)}
          />
        ))}
      </div>
    </div>
  );
};

const Articles = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentSEO = SEO.find((item) => item.page === "articles");

  return (
    <React.Fragment>
      <Helmet>
        <title>{`Why Us? | ${INFO.main.title}`}</title>
        <meta name="description" content={currentSEO.description} />
        <meta
          name="keywords"
          content={currentSEO.keywords.join(", ")}
        />
      </Helmet>

      <div className="page-content">
        <NavBar active="articles" />
        <div className="content-wrapper">
          <div className="articles-logo-container">
            <div className="articles-logo">
              <Logo width={46} />
            </div>
          </div>

          <div className="articles-main-container">
            <div className="why-us-header">
              <div className="why-us-main-title">
                Why SB Statistical Consulting?
              </div>
              <div className="why-us-subtitle">
                We deliver <strong>insights you can trust with impacts you can measure.</strong>
              </div>
            </div>

            {/* Animated Statistics */}
            <div className="stats-section">
              <div className="stat-item">
                <div className="stat-number">
                  <AnimatedCounter end={5} suffix="+" />
                </div>
                <div className="stat-label">Years Combined Experience</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">
                  <AnimatedCounter end={3} />
                </div>
                <div className="stat-label">Degrees</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">
                  <AnimatedCounter end={6} />
                </div>
                <div className="stat-label">Industries</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">
                  <AnimatedCounter end={100} suffix="%" />
                </div>
                <div className="stat-label">Here to help</div>
              </div>
            </div>



            {/* Updated Problem-Solution Section */}
            <div className="problem-solution-section">
              {/* The Challenge Section */}
              <div className="challenge-section">
                <div className="section-header">
                  <div className="section-icon challenge-icon">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                  </div>
                  <h3 className="section-title-small">The Challenge</h3>
                </div>
                <div className="challenge-content">
                  <p className="challenge-text">
                    Many organizations and small businesses need <strong>quality statistical analysis</strong> but lack the budget for full-time statisticians or dedicated data science departments.
                  </p>
                  <div className="challenge-problems">
                    <div className="problem-item">
                      <FontAwesomeIcon icon={faTimes} className="problem-icon" />
                      <span>Limited statistical expertise in-house</span>
                    </div>
                    <div className="problem-item">
                      <FontAwesomeIcon icon={faTimes} className="problem-icon" />
                      <span>Budget constraints for full-time hires</span>
                    </div>
                    <div className="problem-item">
                      <FontAwesomeIcon icon={faTimes} className="problem-icon" />
                      <span>Difficulty translating analysis into action</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Visual Separator */}
              <div className="solution-separator">
                <div className="separator-line"></div>
                <div className="separator-arrow">
                  <FontAwesomeIcon icon={faArrowDown} />
                </div>
                <div className="separator-line"></div>
              </div>

              {/* Our Solution Section */}
              <div className="solution-section">
                <div className="section-header">
                  <div className="section-icon solution-icon">
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </div>
                  <h3 className="section-title-small">Our Solution</h3>
                </div>
                <div className="solution-content">
                  <p className="solution-intro">
                    We bring a <strong>unique combination of academic excellence, teaching experience, and industry expertise</strong> that sets us apart from traditional statistical consultants.
                  </p>
                  
                  <div className="solution-pillars">
                    <div className="pillar">
                      <div className="pillar-header">
                        <FontAwesomeIcon icon={faGraduationCap} className="pillar-icon" />
                        <h4 className="pillar-title">Academic Excellence</h4>
                      </div>
                      <div className="pillar-content">
                        <div className="pillar-highlight">
                          <strong>3 degrees</strong> in statistics, economics, and applied statistics
                        </div>
                        <div className="pillar-detail">
                          Including a <strong>master's degree with departmental honors</strong>. Both partners graduated with <strong>honors and multiple academic awards</strong>.
                        </div>
                      </div>
                    </div>

                    <div className="pillar">
                      <div className="pillar-header">
                        <FontAwesomeIcon icon={faChalkboardTeacher} className="pillar-icon" />
                        <h4 className="pillar-title">Teaching Experience</h4>
                      </div>
                      <div className="pillar-content">
                        <div className="pillar-highlight">
                          <strong>University-level instruction</strong> in statistical methods
                        </div>
                        <div className="pillar-detail">
                          This experience sharpened our ability to <strong>translate technical analysis for non-technical audiences</strong>.
                        </div>
                      </div>
                    </div>

                    <div className="pillar">
                      <div className="pillar-header">
                        <FontAwesomeIcon icon={faBriefcase} className="pillar-icon" />
                        <h4 className="pillar-title">Industry Expertise</h4>
                      </div>
                      <div className="pillar-content">
                        <div className="pillar-highlight">
                          <strong>5+ years combined experience</strong> across multiple industries
                        </div>
                        <div className="pillar-detail">
                          Roles at <strong>leading financial institutions and tech companies</strong>, working on AI/NLP development, predictive modeling, advanced analytics, and data visualization.
                        </div>
                      </div>
                    </div>

                    <div className="pillar">
                      <div className="pillar-header">
                        <FontAwesomeIcon icon={faFileAlt} className="pillar-icon" />
                        <h4 className="pillar-title">Proven Results</h4>
                      </div>
                      <div className="pillar-content">
                        <div className="pillar-highlight">
                          <strong>Research experience</strong> and measurable outcomes
                        </div>
                        <div className="pillar-detail">
                          Co-authored paper in healthcare field under review for publication, plus experience designing data pipelines and <strong>translating findings into actionable strategies</strong>.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="solution-outcome">
                    <div className="outcome-box">
                      <FontAwesomeIcon icon={faBullseye} className="outcome-icon" />
                      <div className="outcome-text">
                        <strong>The Result:</strong> When you work with us, you're not just getting analysis—you're getting <strong>clear, actionable insights</strong> that help you <strong>turn data into decisions that drive measurable results</strong>.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

                        {/* Testimonials Section */}
            <div className="testimonials-section">
              <h2 className="section-title">What Our Clients Say</h2>
              <TestimonialsCarousel />
            </div>

          </div>
          <div className="page-footer">
            <Footer />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Articles;